import React from "react";
import { useNavigate } from "react-router-dom";
import { Plug, Database, CalendarClock, MessageSquare, Mic, Mail, CreditCard, CheckCircle2, XCircle, type LucideIcon } from "lucide-react";
import { StatusBadge } from "@/components/ds";
import { SectionCard } from "./SectionCard";
import { useAuth } from "@/context/AuthContext";
import type { DashboardData } from "@/lib/api";

const ICONS: Record<string, LucideIcon> = {
  supabase: Database,
  google_calendar: CalendarClock,
  twilio: MessageSquare,
  voice: Mic,
  email: Mail,
  stripe: CreditCard,
};

const PLATFORM_ADMIN_ROLES = ["senior_access_admin", "platform_admin"];

export function IntegrationHealth({ items }: { items: DashboardData["integrations"] }) {
  const navigate = useNavigate();
  const { user } = useAuth() as any;
  // Platform Infrastructure is Senior/Platform Admin only — hide from customers & sales reps.
  if (!PLATFORM_ADMIN_ROLES.includes(user?.platform_role)) return null;
  const connected = items.filter((i) => i.status === "connected").length;
  return (
    <SectionCard
      title="Platform infrastructure readiness"
      icon={Plug}
      testid="integration-health-card"
      action={<span className="text-xs font-medium text-slate-400" data-testid="integration-count">{connected}/{items.length} operational</span>}
    >
      <div className="grid gap-px bg-slate-100 sm:grid-cols-2">
        {items.map((it) => {
          const Icon = ICONS[it.key] ?? Plug;
          const ok = it.status === "connected";
          const label = it.key === "supabase" ? "Database — MongoDB" : it.label;
          return (
            <div key={it.key} className="flex items-center gap-3 bg-white px-5 py-4" data-testid={`integration-${it.key}`}>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${ok ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{label}</p>
                {it.provider && it.key !== "supabase" && <p className="truncate text-xs text-slate-400">{it.provider}</p>}
              </div>
              <button onClick={() => navigate("/internal/infrastructure")} data-testid={`integration-connect-${it.key}`}>
                {ok ? (
                  <StatusBadge tone="green" data-testid={`integration-status-${it.key}`} className="transition-colors hover:border-blue-300">
                    <CheckCircle2 className="h-3 w-3" /> {it.detail ?? "Operational"}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone="slate" className="transition-colors hover:border-blue-300 hover:text-blue-700">
                    <XCircle className="h-3 w-3" /> Disconnected
                  </StatusBadge>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
